import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Spinner } from 'react-bootstrap';
import { Chart } from 'react-google-charts';
import { showToast as toast } from '../toastManager';
import axios from 'axios';
import { useQueue } from '../context/QueueContext';

const ITEMS_PER_PAGE = 20;

const YoutubeIntegration = ({ ViewedPage, shop_domain, app_url, isInSettingsContainer }) => {
  useEffect(() => {
    ViewedPage(46);
  }, []);

  const { addJob, jobs: queueJobs } = useQueue();

  const [channelUrl, setChannelUrl] = useState('');
  const [yearsLimit, setYearsLimit] = useState(1);
  const [youtubeFetched, setYoutubeFetched] = useState(false);
  const [youtubeFetchInProgress, setYoutubeFetchInProgress] = useState(false);
  const [youtubeJobId, setYoutubeJobId] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const loadData = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${app_url}/api/settings/youtube/status`, { withCredentials: true });
      if (resp.data.success) {
        setChannelUrl(resp.data.youtube_channel_url || '');
        setYoutubeFetched(resp.data.youtube_fetched || false);
        setYoutubeFetchInProgress(resp.data.youtube_fetch_in_progress || false);
        setVideos(resp.data.videos || []);
        setCurrentPage(1);
      }
    } catch {
      toast.error('Error loading YouTube data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shop_domain && app_url) loadData();
  }, [shop_domain, app_url]);

  // Watch queue job for completion
  useEffect(() => {
    if (!youtubeJobId) return;
    const job = queueJobs.find(j => j.jobId === youtubeJobId);
    if (!job) return;
    if (job.status === 'succeeded') {
      setYoutubeJobId(null);
      setYoutubeFetchInProgress(false);
      setYoutubeFetched(true);
      loadData();
    } else if (job.status === 'failed') {
      setYoutubeJobId(null);
      setYoutubeFetchInProgress(false);
      loadData();
    }
  }, [queueJobs, youtubeJobId]);

  const normalizeChannelUrl = (input) => {
    const trimmed = input.trim();
    if (!trimmed) return trimmed;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    if (trimmed.startsWith('@')) return `https://www.youtube.com/${trimmed}`;
    return `https://www.youtube.com/@${trimmed}`;
  };

  const handleFetchChannel = async () => {
    if (!channelUrl.trim()) { toast.error('Enter a YouTube channel URL first'); return; }
    const normalizedUrl = normalizeChannelUrl(channelUrl);
    let wasQueued = false;
    try {
      setYoutubeFetchInProgress(true);
      const resp = await axios.post(
        `${app_url}/api/settings/youtube/fetch-channel`,
        { channelUrl: normalizedUrl, yearsLimit },
        { withCredentials: true }
      );
      if (resp.status === 202 && resp.data?.jobId) {
        addJob(resp.data.jobId, 'youtube-fetch', `YouTube — ${channelUrl.trim()}`, channelUrl.trim(), app_url);
        setYoutubeJobId(resp.data.jobId);
        wasQueued = true;
        return;
      }
      toast.error(resp.data.message || 'Fetch failed');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error fetching YouTube channel');
    } finally {
      if (!wasQueued) setYoutubeFetchInProgress(false);
    }
  };

  // Pagination
  const totalPages = Math.ceil(videos.length / ITEMS_PER_PAGE);
  const safePage = Math.min(currentPage, Math.max(1, totalPages));
  const pageVideos = videos.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const pageNumberItems = totalPages > 1
    ? Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
        .reduce((acc, p, i, arr) => {
          if (i > 0 && p - arr[i - 1] > 1) acc.push(`ellipsis-${p}`);
          acc.push(p);
          return acc;
        }, [])
    : [];

  const buildTableData = () => {
    if (pageVideos.length === 0) return [['#', 'Thumbnail', 'Title', 'Description', 'Link']];
    const rows = pageVideos.map((video, index) => {
      const num = (safePage - 1) * ITEMS_PER_PAGE + index + 1;
      const safeTitle = (video.title || '').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const thumb = video.thumbnail_url
        ? `<img src="${video.thumbnail_url}" alt="${safeTitle}" style="width:90px;height:50px;object-fit:cover;border-radius:4px" />`
        : `<div style="width:90px;height:50px;background:#eee;border-radius:4px"></div>`;
      const rawDesc = (video.description || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const descHtml = video.description
        ? `<div style="font-size:12px;color:#666;max-width:260px">${rawDesc.substring(0, 120)}${rawDesc.length > 120 ? '...' : ''}</div>`
        : '<span style="color:#999;font-size:12px">No description</span>';
      const link = `<a href="${video.url}" target="_blank" rel="noopener noreferrer" style="font-size:12px">Watch</a>`;
      return [num, thumb, video.title || '', descHtml, link];
    });
    return [['#', 'Thumbnail', 'Title', 'Description', 'Link'], ...rows];
  };

  return (
    <Container fluid className="p-0">
      <div className='main-title mt-4'>
        <h2>YouTube Integration</h2>
      </div>

      <div className="main-accordion-body mt-4 p-2">

        {/* ── Connect Channel ── */}
        <Card className="mb-3 asset-form-card">
          <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
            <div className="sxi-accordion-header-with-icon">
              <div className="sxi-accordion-header-icon" aria-hidden="true">
                <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                  <rect x="3" y="3" width="48" height="48" rx="6" fill="#FFF0F0" stroke="#FFCECE"/>
                  <path d="M43.495 22.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501a3.007 3.007 0 0 0-2.088 2.088 31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM29.609 31.601V24.408l6.264 3.602z" fill="#FF0000"/>
                </svg>
              </div>
              <span>Connect YouTube Channel</span>
            </div>
          </Card.Header>
          <Card.Body>
            {youtubeFetched && channelUrl && (
              <div className="d-flex align-items-center gap-2 flex-wrap mb-3 px-3 py-2" style={{ background: '#F9FAFB', borderRadius: 8, border: '1px solid #EAECF0' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#FF0000">
                  <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
                </svg>
                <span className="text-muted small" style={{ wordBreak: 'break-all' }}>{channelUrl}</span>
                <span style={{ fontSize: 12, color: '#344054', background: '#F2F4F7', border: '1px solid #D0D5DD', borderRadius: 100, padding: '2px 10px' }}>{videos.length} videos imported</span>
              </div>
            )}
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <Form.Control
                type="text"
                placeholder="@yourchannel or https://www.youtube.com/@yourchannel"
                value={channelUrl}
                onChange={e => setChannelUrl(e.target.value)}
                disabled={youtubeFetchInProgress}
                style={{ minWidth: 260 }}
              />
              <div className="d-flex align-items-center gap-1" style={{ whiteSpace: 'nowrap' }}>
                <Form.Label className="mb-0 text-muted small">Last</Form.Label>
                <Form.Control
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={yearsLimit}
                  onChange={e => setYearsLimit(parseFloat(e.target.value) || 1)}
                  disabled={youtubeFetchInProgress}
                  style={{ width: 70 }}
                />
                <Form.Label className="mb-0 text-muted small">yr(s)</Form.Label>
              </div>
              <Button
                className="sxi_primary_blue_btn btn-height"
                onClick={handleFetchChannel}
                disabled={youtubeFetchInProgress || !channelUrl.trim()}
                style={{ whiteSpace: 'nowrap' }}
              >
                {youtubeFetchInProgress ? (
                  <><Spinner size="sm" animation="border" className="me-2" />Fetching...</>
                ) : youtubeFetched ? 'Re-fetch Videos' : 'Fetch Videos'}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* ── Imported Videos ── */}
        <Card className="mb-4 asset-form-card">
          <Card.Header className="py-3" style={{ background: 'white', borderBottom: '1px solid #EAECF0' }}>
            <div className="sxi-accordion-header-with-icon">
              <div className="sxi-accordion-header-icon" aria-hidden="true">
                <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                  <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" stroke="#CEDEFF"/>
                  <rect x="15" y="17" width="28" height="24" rx="2" stroke="#2056C1" strokeWidth="1.5" fill="none"/>
                  <line x1="15" y1="23" x2="43" y2="23" stroke="#2056C1" strokeWidth="1.5"/>
                  <line x1="24" y1="17" x2="24" y2="41" stroke="#2056C1" strokeWidth="1.5"/>
                </svg>
              </div>
              <span>
                Imported Videos
                {videos.length > 0 && <span style={{ fontSize: 12, color: '#667085', background: '#F2F4F7', border: '1px solid #D0D5DD', borderRadius: 100, padding: '2px 10px', marginLeft: 8 }}>{videos.length}</span>}
              </span>
            </div>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : videos.length === 0 ? (
              <p className="text-muted mb-0">
                {youtubeFetchInProgress
                  ? 'Fetching videos...'
                  : 'No videos imported yet.'}
              </p>
            ) : (
              <>
                <div className="personalization-targeting-table">
                  <Chart
                    chartType="Table"
                    data={buildTableData()}
                    options={{
                      cssClassNames: {
                        tableCell:   'channel_performance_table_cell',
                        headerCell:  'channel_performance_header_cell',
                        tableRow:    'channel_performance_table_row',
                        oddTableRow: 'channel_performance_old_table_row',
                      },
                      allowHtml: true,
                      page: 'disable',
                    }}
                    width="100%"
                  />
                </div>

                {totalPages > 1 && (
                  <div className="opportunities-table-pagination personalization-targeting-content-pagination mt-3">
                    <button
                      className="opportunities-page-nav opportunities-page-nav-prev"
                      disabled={safePage === 1}
                      onClick={() => setCurrentPage(p => p - 1)}
                    >
                      Previous
                    </button>
                    <nav className="d-flex align-items-center gap-1 opportunities-page-numbers">
                      {pageNumberItems.map(p =>
                        typeof p === 'string'
                          ? <span key={p} className="px-2">…</span>
                          : <button
                              key={p}
                              className={p === safePage ? 'acitve-pagination' : 'pagination-outline'}
                              style={{ width: 35, height: 35, borderRadius: 8, backgroundColor: '#F9FAFB', border: '1px solid #EAECF0' }}
                              onClick={() => setCurrentPage(p)}
                            >{p}</button>
                      )}
                    </nav>
                    <button
                      className="opportunities-page-nav opportunities-page-nav-next"
                      disabled={safePage === totalPages}
                      onClick={() => setCurrentPage(p => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>

      </div>
    </Container>
  );
};

export default YoutubeIntegration;
